<script lang="ts" setup>
import type {
	MapInstanceRef,
	MarkerInstanceRef
} from '@/core/service/googleMaps/types.d';

import {
	bindEvents,
	bindProps,
	getComponentEventsConfig,
	getComponentPropsConfig,
	getPropsValuesWithoutOptionsProp
} from '@/core/service/googleMaps/utils';
import {
	clusterKey as clusterKeySymbol,
	mapPromiseKey
} from '@/core/service/googleMaps/utils/symbols';
import { useGoogleMaps } from '~/composables/useGoogleMaps';
import { h, inject, onUnmounted, render, watch } from 'vue';

import IconMaterialSymbolsLocationOn from '~icons/material-symbols-light/location-on';

defineOptions({ name: 'GmapMarker' });

/**
 * Marker component
 * @displayName GmvMarker
 * @see [source code](/guide/marker.html#source-code)
 * @see [Official documentation](https://developers.google.com/maps/documentation/javascript/markers)
 * @see [Official reference](https://developers.google.com/maps/documentation/javascript/reference/marker)
 */

type MarkerVariant = 'black' | 'red';
type MarkerSize = 'small' | 'default';

interface VariantConfig {
	color: string;
	useSvg?: boolean;
}

const variantConfigs: Record<MarkerVariant, VariantConfig> = {
	black: {
		color: '#000000'
	},
	red: {
		color: '#EA4335',
		useSvg: true
	}
};

const sizeConfigs: Record<
	MarkerSize,
	{ svgPath: string; width: string; height: string }
> = {
	small: {
		svgPath: '/images/map/marker_small.svg',
		width: '24px',
		height: '33px'
	},
	default: {
		svgPath: '/images/map/marker_default.svg',
		width: '28px',
		height: '44px'
	}
};

const props = withDefaults(
	defineProps<{
		collisionBehavior?: google.maps.CollisionBehavior;
		content?: HTMLElement;
		gmpClickable?: boolean;
		gmpDraggable?: boolean;
		position?:
			| google.maps.LatLng
			| google.maps.LatLngLiteral
			| google.maps.LatLngAltitude
			| google.maps.LatLngAltitudeLiteral;
		title?: string;
		zIndex?: number;
		markerKey?: string;
		clusterKey?: string;
		options?: Record<string, unknown>;
		variant?: MarkerVariant;
		size?: MarkerSize;
		address?: string;
	}>(),
	{
		gmpClickable: true,
		gmpDraggable: true,
		variant: 'black',
		size: 'default'
	}
);

const emits = defineEmits<{
	click: [value: google.maps.MapMouseEvent];
	drag: [value: google.maps.MapMouseEvent];
	dragEnd: [value: google.maps.MapMouseEvent];
	dragStart: [value: google.maps.MapMouseEvent];
	gmpClick: [value: google.maps.marker.AdvancedMarkerClickEvent];
	'update:position': [value: { lat?: number; lng?: number }];
}>();

const mapPromise = inject(mapPromiseKey);
const mapInstance: MapInstanceRef = ref();
const clusterInstance = inject(clusterKeySymbol, ref());
const markerInstance: Ref<
	google.maps.marker.AdvancedMarkerElement | undefined | null
> = ref();
const { api: googleMapsApi } = useGoogleMaps();
const isGoogleMapsLoaded = ref(false);

function createMarkerIcon(config: VariantConfig) {
	const iconContainer = document.createElement('div');
	const iconVNode = h(IconMaterialSymbolsLocationOn, {
		style: {
			width: '36x',
			height: '36px',
			color: config.color,
			fontSize: '36px'
		}
	});

	render(iconVNode, iconContainer);
	return iconContainer;
}

function createMarkerContainer() {
	const container = document.createElement('div');
	container.style.display = 'flex';
	container.style.flexDirection = 'column';
	container.style.alignItems = 'center';
	container.style.position = 'relative';
	return container;
}

/**
 * Creates an address label with absolute positioning
 */
function createAddressLabel(text: string, color: string) {
	const addressElement = document.createElement('div');
	addressElement.textContent = text;
	addressElement.style.color = '#000000';
	addressElement.style.fontSize = '14px';
	addressElement.style.fontWeight = 'bold';
	addressElement.style.textAlign = 'center';
	addressElement.style.marginTop = '2px';
	addressElement.style.maxWidth = '124px';
	addressElement.style.width = '124px';
	addressElement.style.whiteSpace = 'normal';
	addressElement.style.overflow = 'hidden';
	addressElement.style.textOverflow = 'ellipsis';
	addressElement.style.position = 'absolute';
	addressElement.style.top = '100%';
	addressElement.style.left = '50%';
	addressElement.style.transform = 'translateX(-50%)';
	addressElement.style.textShadow =
		'-1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white';
	addressElement.style.lineHeight = '14px';
	addressElement.style.maxHeight = '28px';
	addressElement.style.wordWrap = 'break-word';
	return addressElement;
}

function createCustomMarker(variant: MarkerVariant = 'black') {
	if (typeof document === 'undefined') return null;

	const config = variantConfigs[variant];
	const container = createMarkerContainer();

	const iconElement = createMarkerIcon(config);
	container.appendChild(iconElement);

	if (props.address) {
		container.appendChild(createAddressLabel(props.address, config.color));
	}

	return container;
}

/**
 * Creates a custom SVG marker using the available SVG files
 */
function createSvgMarker(
	variant: MarkerVariant = 'black',
	size: MarkerSize = 'default'
) {
	if (typeof document === 'undefined') return null;

	const config = variantConfigs[variant];
	const sizeConfig = sizeConfigs[size];
	const container = createMarkerContainer();

	const svgElement = document.createElement('img');
	svgElement.src = sizeConfig.svgPath;
	svgElement.style.width = sizeConfig.width;
	svgElement.style.height = sizeConfig.height;
	svgElement.style.display = 'block';

	container.appendChild(svgElement);

	if (props.address) {
		container.appendChild(createAddressLabel(props.address, config.color));
	}

	return container;
}

function createMarkerContent() {
	if (props.content) return props.content;

	const config = variantConfigs[props.variant];

	// Use custom SVG markers for variants that specify useSvg
	if (config.useSvg) {
		return createSvgMarker(props.variant, props.size);
	}

	return createCustomMarker(props.variant);
}

async function initMarker() {
	try {
		if (!googleMapsApi.value?.marker) {
			console.warn('Google Maps API or marker library not available yet');
			return;
		}

		isGoogleMapsLoaded.value = true;

		const { AdvancedMarkerElement } = googleMapsApi.value.marker;

		const markerOptions = createMarkerOptions(AdvancedMarkerElement);
		markerInstance.value = new AdvancedMarkerElement(markerOptions);

		bindComponent();
		addToClusterIfNeeded();
		setupEventListeners();
	} catch (error) {
		console.error('Error initializing marker:', error);
	}
}

/**
 * Creates the marker options
 */
function createMarkerOptions(AdvancedMarkerElement: any) {
	const baseOptions = {
		map: clusterInstance.value ? undefined : toRaw(mapInstance.value),
		...getPropsValuesWithoutOptionsProp(props),
		...props.options
	};

	const markerOptions: any = {
		...baseOptions,
		gmpDraggable: false,
		zIndex: props.zIndex || 1000,
		content: createMarkerContent()
	};

	// Set anchor point to ensure the pin points to the correct location
	const advancedMarkerWithAnchor = AdvancedMarkerElement as unknown as {
		Anchor?: { BOTTOM: unknown };
	};
	if (advancedMarkerWithAnchor.Anchor) {
		markerOptions.anchor = advancedMarkerWithAnchor.Anchor.BOTTOM;
	}

	delete markerOptions.address;
	delete markerOptions.variant;
	delete markerOptions.size;

	return markerOptions;
}

/**
 * Adds the marker to the cluster if needed
 */
function addToClusterIfNeeded() {
	if (clusterInstance.value && markerInstance.value) {
		clusterInstance.value.addMarker(markerInstance.value);
	}
}

function bindComponent() {
	if (!markerInstance.value) return;

	const markerIconPropsConfig = getComponentPropsConfig('GmpMarker');
	const markerIconEventsConfig = getComponentEventsConfig('GmpMarker', 'auto');

	bindProps(markerIconPropsConfig, markerInstance.value, emits as any, props);
	bindEvents(markerIconEventsConfig, markerInstance.value, emits as any);
}

function setupEventListeners() {
	if (!markerInstance.value) return;

	markerInstance.value.addEventListener('gmp-click', e => {
		emits('click', e as unknown as google.maps.MapMouseEvent);
	});

	markerInstance.value.addListener('dragend', () => {
		const newPosition = markerInstance.value?.position;
		emits('update:position', {
			lat:
				typeof newPosition?.lat === 'number'
					? newPosition.lat
					: newPosition?.lat?.(),
			lng:
				typeof newPosition?.lng === 'number'
					? newPosition.lng
					: newPosition?.lng?.()
		});
	});

	if (mapInstance.value) {
		mapInstance.value.addListener('bounds_changed', () => {
			if (!markerInstance.value || clusterInstance.value) {
				return;
			}

			const bounds = mapInstance.value?.getBounds();
			const markerPosition = markerInstance.value.position;

			if (markerPosition && bounds) {
				const isMarkerInBounds = bounds.contains(markerPosition);
				markerInstance.value.map = isMarkerInBounds
					? toRaw(mapInstance.value)
					: null;
			}
		});
	}
}

function cleanupMarker() {
	try {
		if (!markerInstance.value) return;

		if (clusterInstance.value) {
			clusterInstance.value.removeMarker(markerInstance.value);
		}

		markerInstance.value.map = null;

		google.maps.event.clearInstanceListeners(markerInstance.value);

		markerInstance.value = null;
	} catch (error) {
		console.error('Error cleaning up marker:', error);
	}
}

watch(
	() => [props.position?.lat, props.position?.lng, props.address],
	(newPosition, oldPosition) => {
		const [newLat, newLng, newAddress] = newPosition || [];
		const [oldLat, oldLng, oldAddress] = oldPosition || [];

		if (newLat === oldLat && newLng === oldLng && newAddress === oldAddress) {
			return;
		}

		cleanupMarker();
		initMarker();
	}
);

onBeforeMount(async () => {
	try {
		mapInstance.value = await mapPromise;
		await initMarker();
	} catch (error) {
		console.error('Error mounting marker component:', error);
	}
});

onMounted(() => {
	if (!markerInstance.value && mapInstance.value && googleMapsApi.value) {
		initMarker();
	}
});

onUnmounted(() => {
	try {
		cleanupMarker();
	} catch (error) {
		console.error('Error unmounting marker:', error);
	}
});

defineExpose({ markerInstance });
</script>

<template>
	<div></div>
</template>
